import { ColumnDef } from '@tanstack/react-table'
import type { ClientInvoiceListItem } from '@/api/invoicing'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import { InvoiceStatusBadge } from './invoice-status-badge'
import { Link } from '@tanstack/react-router'
import i18n from '@/i18n'

const fmtMoney = (v: string) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(parseFloat(v))

const fmtDate = (v: string | null) => {
  if (!v) return '—'
  const [y, m, d] = v.split('-')
  return `${d}/${m}/${y}`
}

export const columns: ColumnDef<ClientInvoiceListItem>[] = [
  {
    accessorKey: 'number',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_number')} />
    ),
    cell: ({ row }) => (
      <Link
        // @ts-ignore -- router type augmentation happens at the route level
        to={`/invoices/${row.original.id}`}
        className="font-mono font-semibold text-primary hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {row.original.number}
      </Link>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'client_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_client')} />
    ),
    cell: ({ row }) => (
      <Link
        // @ts-ignore
        to={`/clients/${row.original.client}`}
        className="hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {row.original.client_name}
      </Link>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'issue_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_issue_date')} />
    ),
    cell: ({ row }) => <span>{fmtDate(row.original.issue_date)}</span>,
    enableSorting: true,
  },
  {
    accessorKey: 'due_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_due_date')} />
    ),
    cell: ({ row }) => <span className="text-muted-foreground">{fmtDate(row.original.due_date)}</span>,
    enableSorting: false,
  },
  {
    accessorKey: 'total_ttc',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_total_ttc')} />
    ),
    cell: ({ row }) => <span className="font-semibold">{fmtMoney(row.original.total_ttc)}</span>,
    enableSorting: false,
  },
  {
    accessorKey: 'net_a_payer',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_net')} />
    ),
    cell: ({ row }) => <span className="font-bold">{fmtMoney(row.original.net_a_payer)}</span>,
    enableSorting: true,
  },
  {
    accessorKey: 'reste_a_payer',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_reste')} />
    ),
    cell: ({ row }) => {
      const v = parseFloat(row.original.reste_a_payer)
      return (
        <span className={v > 0 ? 'font-bold text-red-600' : 'font-semibold text-green-600'}>
          {fmtMoney(row.original.reste_a_payer)}
        </span>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('inv_col_status')} />
    ),
    cell: ({ row }) => <InvoiceStatusBadge status={row.original.status} />,
    enableSorting: true,
  },
]
